import React, { useEffect, useState, useRef } from 'react';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { V2Tabs } from '@abyss/web/ui/Tabs';
import { ModalService } from '@src/api/common/ModalService';

type TabAddon = {
  title: string;
  code: string;
  index: number;
};

type TabContainerProps = {
  title: string;
  addons: Array<TabAddon>;
  getTabComponent(tabCode: string): React.JSX.Element | null;
  shouldBlockNavigation?: () => boolean;
};

const TabContainer: React.FC<TabContainerProps> = ({
  title,
  addons,
  getTabComponent,
  shouldBlockNavigation = () => false,
}: TabContainerProps) => {
  const { getLocation, navigate } = useRouter();
  const location = getLocation();
  const [activeIndex, setActiveIndex] = useState(0);
  const nextActionRef = useRef<number | null>(null);
  const { showModal, hideModal } = ModalService();

  useEffect(() => {
    if (location) {
      const pathParts = location.pathname.split('/');
      const tabIndex = pathParts.indexOf('tab');
      if (tabIndex !== -1 && pathParts[tabIndex + 1]) {
        const tabId = pathParts[tabIndex + 1];
        const idx = addons.findIndex((item) => ~~tabId === item.index);
        if (idx !== -1 && idx !== activeIndex) {
          setActiveIndex(idx);
        }
      }
    }
  }, [location, addons]);

  const handleTabChange = (index: number) => {
    if (shouldBlockNavigation()) {
      nextActionRef.current = index; // Store the next tab index
      showModal({
        key: 'unsaved-modal',
        title: 'Unsaved Changes',
        message:
          'You have unsaved changes. Do you want to leave this tab without saving?',
        handleConfirm: async () => {
          await hideModal();
          nextActionRef.current = null;
          navigateToTab(index);
        },
        handleCancel: async () => {
          await hideModal();
          nextActionRef.current = null;
        },
        cancelText: 'Stay',
        submitText: 'Leave',
      });
    } else {
      navigateToTab(index);
    }
  };

  const navigateToTab = (index: number) => {
    if (location) {
      navigate(
        `${location.pathname.split('/tab')[0]}/tab/${addons[index].index}`
      );
      setActiveIndex(index);
    }
  };

  return (
    <V2Tabs title={title} active={activeIndex} onTabChange={handleTabChange}>
      {addons.map((item) => (
        <V2Tabs.Tab key={item.title} label={item.title}>
          {getTabComponent(item.code)}
        </V2Tabs.Tab>
      ))}
    </V2Tabs>
  );
};

export { TabContainer };
